import { ButtonIcon, styled, SyncIcon, Tooltip } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';

import { ValueBox, ValueBoxGroupOptions } from '@candu-ai/craft/ui';
import { WebFontResource } from '@candu-ai/styleguide/custom-fonts';
import { useNavigation } from 'commons/hooks';
import { DropdownBox } from 'craft/containers/Toolbox/components';
import { getCssResetStyleMessage, StylesHierarchy } from 'craft/hooks';
import { useViewportBreakpoint } from 'craft/state';
import { STYLEGUIDE_PATH } from 'dashboard/pages/Styleguide';

import { useStyleguideFonts } from '../useStyleguideFonts';

const StyledDropdownBox = styled(DropdownBox)`
  flex-grow: 1;
  margin-bottom: 0;
`;

const SystemFont: WebFontResource = {
  id: 'system-ui',
  family: 'system-ui',
  type: 'system',
};

interface Props {
  cssProps: Record<string, unknown>;
  setCss: (e: Record<string, unknown>) => void;
  stylesHierarchy: StylesHierarchy;
  isInGroup?: ValueBoxGroupOptions;
  variant?: string;
}

export const FontFamily = ({ cssProps, setCss, isInGroup, stylesHierarchy, variant }: Props) => {
  const { fontFamily } = cssProps;
  const viewport = useViewportBreakpoint();
  const styleguideFonts = useStyleguideFonts();
  const navigate = useNavigation();

  const defaultValue = stylesHierarchy?.fontFamily?.fallbackValue || 'initial';
  const [inputValue, setInputValue] = useState<string>((fontFamily || defaultValue) as string);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setInputValue((fontFamily || defaultValue) as string);
  }, [viewport, variant]);

  const fonts = [...(styleguideFonts || []), SystemFont];

  const items = fonts.map(({ family }) => ({
    name: family,
    value: family,
  }));

  const filteredItems = useMemo(() => {
    if (!searchValue) {
      return items;
    }

    return items.filter((item) => {
      return item.name.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, items]);

  const updateSearchValue = debounce((search: string) => {
    setSearchValue(search);
  }, 300);

  return (
    <ValueBox isInGroup={isInGroup} showOverflow>
      <StyledDropdownBox
        boxCss={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}
        isSearchable
        onSearchChange={updateSearchValue}
        searchInputProps={{
          placeholder: `Search a font`,
        }}
        value={inputValue}
        options={filteredItems}
        onChange={(selectedValue: string) => {
          if (selectedValue) {
            setInputValue(selectedValue);
            setCss({ fontFamily: selectedValue });
          }
        }}
        suffix={
          !!fontFamily && inputValue !== defaultValue ? (
            <Tooltip
              placement="top"
              content={getCssResetStyleMessage(stylesHierarchy?.fontFamily?.fallbackStyle)}
            >
              <ButtonIcon
                icon={SyncIcon}
                color="contrast"
                size="xsmall"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setInputValue(defaultValue as string);
                  setCss({ fontFamily: undefined });
                }}
              />
            </Tooltip>
          ) : undefined
        }
        footerAction={{
          label: 'Not finding a font? Create a new style',
          onClick: () => {
            navigate(`${STYLEGUIDE_PATH}/`, {
              state: {
                componentName: 'Typography',
                conditions: { variant },
                createNewStyle: true,
              },
            });
          },
        }}
      />
    </ValueBox>
  );
};
